<script setup>
import { ref, computed, onBeforeMount } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useThemeAssignmentsStore } from "@/stores/themeAssignments";

const uiStore = useUiStore();
const themeAssignmentsStore = useThemeAssignmentsStore();
const emit = defineEmits(["refresh"]);
const companyId = useCompanyStore().companyId;
let id = ref(null);
let priority = ref(null);
let scheduleFrom = ref(null);
let scheduleTo = ref(null);
let scheduleType = ref({ name: "always", description: "Always" });
const scheduleTypeOptions = ref([
  { name: "always", description: "Always" },
  { name: "weekly", description: "Weekly" },
  { name: "monthly", description: "Monthly" },
  { name: "yearly", description: "Yearly" },
]);

let daysOfWeek = ref([]);
let daysOfMonth = ref([]);
let monthsOfYear = ref([]);

let startTime = ref(null);
let endTime = ref(null);

const daysOfWeekOptions = ref([
  { id: 1, name: "Monday" },
  { id: 2, name: "Tuesday" },
  { id: 3, name: "Wednesday" },
  { id: 4, name: "Thursday" },
  { id: 5, name: "Friday" },
  { id: 6, name: "Saturday" },
  { id: 7, name: "Sunday" },
]);
const daysOfMonthOptions = ref([
  { id: 1, name: "1" },
  { id: 2, name: "2" },
  { id: 3, name: "3" },
  { id: 4, name: "4" },
  { id: 5, name: "5" },
  { id: 6, name: "6" },
  { id: 7, name: "7" },
  { id: 8, name: "8" },
  { id: 9, name: "9" },
  { id: 10, name: "10" },
  { id: 11, name: "11" },
  { id: 12, name: "12" },
  { id: 13, name: "13" },
  { id: 14, name: "14" },
  { id: 15, name: "15" },
  { id: 16, name: "16" },
  { id: 17, name: "17" },
  { id: 18, name: "18" },
  { id: 19, name: "19" },
  { id: 20, name: "20" },
  { id: 21, name: "21" },
  { id: 22, name: "22" },
  { id: 23, name: "23" },
  { id: 24, name: "24" },
  { id: 25, name: "25" },
  { id: 26, name: "26" },
  { id: 27, name: "27" },
  { id: 28, name: "28" },
  { id: 29, name: "29" },
  { id: 30, name: "30" },
  { id: 31, name: "31" },
]);
const monthsOfYearOptions = ref([
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
]);

const getModalInfo = computed(() => uiStore.modalData);
const getModalAction = computed(() => uiStore.modalAction);

let isSubmitted = ref(false);

const resetStoreData = () => {
  uiStore.resetModalStore();
};

const disabledDates = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  return (date) => {
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0); // Reset time to start of day
    return compareDate < today;
  };
});

const disabledScheduleToDates = computed(() => {
  if (!scheduleFrom.value) return;
  const today = new Date(scheduleFrom.value);
  // today.setHours(0, 0, 0, 0); // Reset time to start of day
  return (date) => {
    const compareDate = new Date(date);
    // compareDate.setHours(0, 0, 0, 0); // Reset time to start of day
    return compareDate <= today;
  };
});

const onScheduleTypeChange = () => {
  daysOfWeek.value = [];
  daysOfMonth.value = [];
  monthsOfYear.value = [];
  startTime.value = null;
  endTime.value = null;
};

const changeScheduleFromDates = () => {
  scheduleTo.value = null;
};

const formatTime = (time) => {
  if (!time) return null;
  if (typeof time === "string") return time;
  if (time.hours !== undefined) {
    const hours = String(time.hours).padStart(2, "0");
    const minutes = String(time.minutes).padStart(2, "0");
    const seconds = String(time.seconds || 0).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }
  return null;
};

const onSubmit = () => {
  isSubmitted.value = true;
  let data = {
    company_id: companyId,
    id: id.value,
    data: {
      priority: priority.value,
      schedule_from: scheduleFrom.value,
      schedule_to: scheduleTo.value,
      schedule_type: scheduleType.value ? scheduleType.value.name : null,
      schedule_config: {
        start_time: formatTime(startTime.value),
        end_time: formatTime(endTime.value),
        days_of_week: daysOfWeek.value
          ? daysOfWeek.value.map((day) => day.id)
          : null,
        days_of_month: daysOfMonth.value
          ? daysOfMonth.value.map((day) => day.id)
          : null,
        months_of_year: monthsOfYear.value
          ? monthsOfYear.value.map((month) => month.id)
          : null,
      },
    },
  };

  themeAssignmentsStore
    .editThemeAssignment(data)
    .then(() => {
      isSubmitted.value = false;
      resetStoreData();
      emit("refresh");
    })
    .catch(() => {
      isSubmitted.value = false;
    });
};

onBeforeMount(() => {
  if (getModalAction.value == "edit_theme_assignment") {
    id.value = getModalInfo.value.id;
    scheduleFrom.value = getModalInfo.value.valid_from
      ? new Date(getModalInfo.value.valid_from)
      : null;
    scheduleTo.value = getModalInfo.value.valid_until
      ? new Date(getModalInfo.value.valid_until)
      : null;
    priority.value = getModalInfo.value.priority ? getModalInfo.value.priority : null;
    if (getModalInfo.value.schedule_type) {
      scheduleType.value = scheduleTypeOptions.value.find(
        (type) => type.name == getModalInfo.value.schedule_type
      );
    } else {
      scheduleType.value = scheduleTypeOptions.value[0];
    }

    if (getModalInfo.value.schedule_config) {
      let config = getModalInfo.value.schedule_config;
      daysOfWeek.value = config.days_of_week
        ? daysOfWeekOptions.value.filter((day) =>
            config.days_of_week.includes(day.id)
          )
        : [];
      daysOfMonth.value = config.days_of_month
        ? daysOfMonthOptions.value.filter((day) =>
            config.days_of_month.includes(day.id)
          )
        : [];
      monthsOfYear.value = config.months_of_year
        ? monthsOfYearOptions.value.filter((month) =>
            config.months_of_year.includes(month.id)
          )
        : [];
      startTime.value = config.start_time
        ? {
            hours: parseInt(config.start_time.split(":")[0]),
            minutes: parseInt(config.start_time.split(":")[1]),
            seconds: parseInt(config.start_time.split(":")[2]),
          }
        : null;
      endTime.value = config.end_time
        ? {
            hours: parseInt(config.end_time.split(":")[0]),
            minutes: parseInt(config.end_time.split(":")[1]),
            seconds: parseInt(config.end_time.split(":")[2]),
          }
        : null;
    }
  }
});
</script>

<template>
  <DModal v-if="getModalAction === 'edit_theme_assignment'" :visible="uiStore.modalState" @update:visible="val => { if (!val) resetStoreData() }" :main_classes="[]" :dialog_classes="[]">
    <template #title> Edit Assignment </template>

    <template #body>
      <DInput
        :value="priority != null ? String(priority) : ''"
        @input="(e) => { priority = e.target.value ? parseInt(e.target.value) : null; }"
        label="Priority (Optional)"
        width="100%"
        type="number"
        class="mb-3"
      />

      <div class="flex items-center justify-between mb-3">
        <div class="flex">
          <div class="relative">
            <span v-if="scheduleFrom" class="datepicker-title-span"
              >Schedule From</span
            >
            <VueDatePicker
              id="schedule-from-picker"
              auto-apply
              :clearable="scheduleFrom ? true : false"
              format="dd/MM/yyyy"
              v-model="scheduleFrom"
              :month-change-on-scroll="false"
              type="date"
              mode="date"
              :enable-time-picker="false"
              :disabled-dates="disabledDates"
              @update:model-value="changeScheduleFromDates"
              @time-picker-change="changeScheduleFromDates"
              @cleared="changeScheduleFromDates"
              @closed="changeScheduleFromDates"
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="Schedule From"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="scheduleFrom ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!scheduleFrom" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="scheduleFrom ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="scheduleFrom" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between mb-4">
        <div class="flex">
          <div class="relative">
            <span v-if="scheduleTo" class="datepicker-title-span"
              >Schedule To (Optional)</span
            >
            <VueDatePicker
              id="schedule-to-picker"
              auto-apply
              :clearable="scheduleTo ? true : false"
              format="dd/MM/yyyy"
              v-model="scheduleTo"
              :month-change-on-scroll="false"
              type="date"
              :disabled-dates="disabledScheduleToDates"
              :disabled="!scheduleFrom"
              :class="{ 'disabled-div': !scheduleFrom }"
              mode="date"
              :enable-time-picker="false"
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="Schedule To (Optional)"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="scheduleTo ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!scheduleTo" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="scheduleTo ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="scheduleTo" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>  
      <div class="flex items-center justify-between mb-3">
        <div class="flex">
          <div class="relative">
            <span v-if="startTime" class="datepicker-title-span"
              >Start Time (Optional)</span
            >
            <VueDatePicker
              id="auto-brightness-time"
              auto-apply
              :clearable="startTime ? true : false"
              format="HH:mm"
              v-model="startTime"
              :month-change-on-scroll="false"
              type="time"
              time-picker
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="Start Time (Optional)"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="startTime ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!startTime" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="startTime ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="startTime" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between mb-3">
        <div class="flex">
          <div class="relative">
            <span v-if="endTime" class="datepicker-title-span">End Time (Optional)</span>
            <VueDatePicker
              id="auto-brightness-time"
              auto-apply
              :clearable="endTime ? true : false"
              format="HH:mm"
              v-model="endTime"
              :month-change-on-scroll="false"
              type="time"
              time-picker
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="End Time (Optional)"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="endTime ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!endTime" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="endTime ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="endTime" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="form-field mb-3">
        <label class="field-label" for="edit-assignment-schedule-type">Schedule Type</label>
        <PDropdown
          v-model="scheduleType"
          inputId="edit-assignment-schedule-type"
          :options="scheduleTypeOptions"
          optionLabel="description"
          dataKey="name"
          placeholder="Select Schedule Type"
          class="w-full"
          appendTo="body"
          @change="onScheduleTypeChange"
        />
      </div>

      <div
        v-if="scheduleType && scheduleType.name === 'weekly'"
        class="form-field mb-3"
      >
        <label class="field-label" for="edit-assignment-days-of-week">Days of Week</label>
        <PMultiSelect
          v-model="daysOfWeek"
          inputId="edit-assignment-days-of-week"
          :options="daysOfWeekOptions"
          optionLabel="name"
          dataKey="id"
          placeholder="Select days..."
          class="w-full custom-multiselect"
          panelClass="custom-multiselect-panel"
          display="chip"
          filter
          filterPlaceholder="Search days..."
          appendTo="body"
        />
      </div>

      <div
        v-if="scheduleType && scheduleType.name === 'monthly'"
        class="form-field mb-3"
      >
        <label class="field-label" for="edit-assignment-days-of-month">Days of Month</label>
        <PMultiSelect
          v-model="daysOfMonth"
          inputId="edit-assignment-days-of-month"
          :options="daysOfMonthOptions"
          optionLabel="name"
          dataKey="id"
          placeholder="Select days..."
          class="w-full custom-multiselect"
          panelClass="custom-multiselect-panel"
          display="chip"
          filter
          filterPlaceholder="Search days..."
          appendTo="body"
        />
      </div>

      <div
        v-if="scheduleType && scheduleType.name === 'yearly'"
        class="relative mb-3"
      >
        <span v-if="monthsOfYear.length" class="multiselect-title-span"
          >Months of Year</span
        >
        <VMultiselect
          :class="{ 'multiselect-options': monthsOfYear.length }"
          v-model="monthsOfYear"
          :options="monthsOfYearOptions"
          :disabled="!monthsOfYearOptions.length"
          :loading="!monthsOfYearOptions.length"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          open-direction="bottom"
          placeholder="Months of Year"
          trackBy="id"
          label="name"
          :multiple="true"
        />
      </div>
    </template>
    <template #footer>
      <div class="w-full">
        <div class="seperator text-center"></div>
        <div class="flex mt-20 justify-center">
          <button
            :disabled="
              
              !scheduleType ||
              (scheduleType &&
                scheduleType.name === 'weekly' &&
                !daysOfWeek.length) ||
              (scheduleType &&
                scheduleType.name === 'monthly' &&
                !daysOfMonth.length) ||
              (scheduleType &&
                scheduleType.name === 'yearly' &&
                !monthsOfYear.length) ||
              !scheduleFrom ||
              
              isSubmitted
            "
            @click="onSubmit"
            type="button"
            class="btn btn-primary add-button mr-30"
          >
            Edit
            <span
              v-if="isSubmitted"
              class="spinner-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </button>
          <button
            @click="resetStoreData"
            type="button"
            class="btn btn-secondary cancel-button"

          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </DModal>
</template>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
}
</style>
